import { Button, IconButton } from '@ui/shared/Button';
import intersection from 'lodash/intersection';
import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import {
  Entity,
  EntityType,
  IAthlete,
  IGroup,
  ITeam,
} from '../../../data/types';
import { downloadRoster } from '../../../utils/export';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { Card } from '../../shared/card/Card';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter } from '../../shared/card/CardFooter';
import { CardHeader } from '../../shared/card/CardHeader';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { AthleteList } from './athletes/AthleteList';
import { EntityMenu } from './EntityMenu';
import { EntityTypeMenu } from './EntityTypeMenu';
import { GroupList } from './groups/GroupList';
import { TeamList } from './teams/TeamsList';

interface IProps extends RouteComponentProps {
  athletes: IAthlete[];
  groups: IGroup[];
  groupsLoading: boolean;
  isAdmin?: boolean;
  loading: boolean;
  onChange: (entityType: string, id: string) => void;
  selectedAthleteId: string;
  selectedEntity: EntityType;
  selectedGroupId: string;
  selectedId: string;
  selectedTeamId: string;
  teams: ITeam[];
  teamsLoading: boolean;
}

export const EntitySelector = withRouter(
  (props: IProps): React.ReactElement => {
    const isMobile = useMediaQuery({ maxWidth: 990 });
    /**
     * @description component state
     */
    const [teamSearch, setTeamSearch] = React.useState<string>(String());
    const [groupSearch, setGroupSearch] = React.useState<string>(String());
    const [groupsFilterTeams, setGroupsFilterTeams] = React.useState<string[]>(
      []
    );
    const [athleteSearch, setAthleteSearch] = React.useState<string>(String());
    const [athleteFilterGroups, setAthleteFilterGroups] = React.useState<
      string[]
    >([]);
    const [athleteFilterTeams, setAthleteFilterTeams] = React.useState<
      string[]
    >([]);
    const [open, setOpen] = React.useState<boolean>(false);

    /**
     * @description toggles menu on mobile
     */
    const toggle = (): void => setOpen(!open);

    /**
     * @description clears the current filter for the selected entity
     */
    const clearFilter = (): void => {
      switch (props.selectedEntity) {
        case 'teams':
          setTeamSearch('');
          break;
        case 'groups':
          setGroupSearch('');
          setGroupsFilterTeams([]);
          break;
        case 'athletes':
          setAthleteSearch('');
          setAthleteFilterGroups([]);
          setAthleteFilterTeams([]);
          break;
      }
    };

    const exportRosters = (): void => {
      downloadRoster(props.athletes, props.groups, props.teams);
    };

    /**
     * @description flags that if a given filter is currently applied
     */
    const teamFilterApplied = Boolean(teamSearch?.length);
    const groupFilterApplied = Boolean(
      groupSearch?.length || groupsFilterTeams?.length
    );
    const athleteFilterApplied = Boolean(
      athleteSearch?.length ||
        athleteFilterTeams?.length ||
        athleteFilterGroups?.length
    );

    /**
     * @description filters teams based on the current filter
     */
    const filteredTeams = React.useMemo(
      (): Entity[] => filterEntities(teamSearch, [], [], props.teams),
      [teamSearch, props.teams]
    );

    /**
     * @description filters groups based on the current filter
     */
    const filteredGroups = React.useMemo(
      (): Entity[] =>
        filterEntities(groupSearch, groupsFilterTeams, [], props.groups),
      [groupSearch, groupsFilterTeams, props.groups]
    );

    /**
     * @description teams to show in GroupList - only filtered teams if filter is applied
     */
    const teamsForGroupList = React.useMemo(
      (): ITeam[] =>
        groupsFilterTeams.length > 0
          ? props.teams.filter((t: ITeam): boolean =>
              groupsFilterTeams.includes(t.id)
            )
          : props.teams,
      [groupsFilterTeams, props.teams]
    );

    /**
     * @description teams to show in AthleteList - only filtered teams if filter is applied
     */
    const teamsForAthleteList = React.useMemo(
      (): ITeam[] =>
        athleteFilterTeams.length > 0
          ? props.teams.filter((t: ITeam): boolean =>
              athleteFilterTeams.includes(t.id)
            )
          : props.teams,
      [athleteFilterTeams, props.teams]
    );

    /**
     * @description filters athletes based on the current filter
     */
    const filteredAthletes = React.useMemo(
      (): Entity[] =>
        filterEntities(
          athleteSearch,
          athleteFilterTeams,
          athleteFilterGroups,
          props.athletes
        ),
      [athleteSearch, athleteFilterTeams, athleteFilterGroups, props.athletes]
    );

    /**
     * @description handles user selecting an athlete
     * @param id
     */
    const goToAthlete = (id: string): void => {
      toggle();
      props.history.push(`/athletes/${id}`);
      props.onChange('athletes', id);
    };
    /**
     * @description handles user selecting a team
     * @param id
     */
    const goToTeam = (id: string): void => {
      toggle();
      props.history.push(`/teams/${id}`);
      props.onChange('teams', id);
    };
    /**
     * @description handles user selecting a group
     * @param id
     */
    const goToGroup = (id: string): void => {
      toggle();
      props.history.push(`/groups/${id}`);
      props.onChange('groups', id);
    };

    /**
     * @description renders the list for the currently selected entityType
     */
    const renderList = (): React.ReactElement => {
      if (props.loading) return <LinearLoading />;
      switch (props.selectedEntity) {
        case 'teams':
          return (
            <TeamList
              isMobile={isMobile}
              teams={filteredTeams as ITeam[]}
              loading={props.loading}
              isAdmin={props.isAdmin}
              selectedTeamId={props.selectedId}
              onTeamChange={goToTeam}
              filterApplied={teamFilterApplied}
              clearFilter={clearFilter}
            />
          );
        case 'groups':
          return (
            <GroupList
              isMobile={isMobile}
              teams={teamsForGroupList}
              groups={filteredGroups as IGroup[]}
              loading={props.loading}
              selectedGroupId={props.selectedId}
              onGroupChange={goToGroup}
              filterApplied={groupFilterApplied}
              clearFilter={clearFilter}
            />
          );

        case 'athletes':
          return (
            <AthleteList
              isMobile={isMobile}
              teams={teamsForAthleteList}
              athletes={filteredAthletes as IAthlete[]}
              loading={props.loading}
              selectedAthleteId={props.selectedId}
              onAthleteChange={goToAthlete}
              filterApplied={athleteFilterApplied}
              clearFilter={clearFilter}
            />
          );
      }
    };

    /**
     * @description returns the current search term and setter for the currently selected entity
     */
    const getCurrentSearch = (): {
      term: string;
      setter: (v: string) => void;
    } => {
      switch (props.selectedEntity) {
        case 'athletes':
          return { term: athleteSearch, setter: setAthleteSearch };

        case 'groups':
          return { term: groupSearch, setter: setGroupSearch };

        case 'teams':
          return { term: teamSearch, setter: setTeamSearch };
      }
    };

    const currentSearch = getCurrentSearch();

    return (
      <Wrapper open={open} toggle={toggle} isMobile={isMobile}>
        <PositionedCard>
          <Column>
            <CardHeader style={{ padding: '0px' }}>
              <Column>
                <Row style={{ minHeight: '50px' }}>
                  <EntityTypeMenu
                    selectedAthleteId={props.selectedAthleteId}
                    selectedGroupId={props.selectedGroupId}
                    selectedTeamId={props.selectedTeamId}
                  />
                </Row>
                <SelectedEntityLabel>
                  {props.selectedEntity.charAt(0).toUpperCase() + props.selectedEntity.slice(1)}
                </SelectedEntityLabel>
                <Row>
                  <EntityMenu
                    entityType={props.selectedEntity}
                    onSearch={currentSearch.setter}
                    search={currentSearch.term}
                    groups={props.groups}
                    teams={props.teams}
                    teamsLoading={props.teamsLoading}
                    groupsLoading={props.groupsLoading}
                    athleteFilterGroups={athleteFilterGroups}
                    athleteFilterTeams={athleteFilterTeams}
                    setAthleteFilterGroups={setAthleteFilterGroups}
                    setAthleteFilterTeams={setAthleteFilterTeams}
                    groupsFilterTeams={groupsFilterTeams}
                    setGroupFilterTeams={setGroupsFilterTeams}
                    athletes={props.athletes}
                    isAdmin={props.isAdmin}
                  />
                </Row>
              </Column>
            </CardHeader>
            <CardBody>{renderList()}</CardBody>
            <CardFooter style={{ justifyContent: 'center' }} surface={8}>
              <Button
                icon='assignment'
                title='rosters'
                onClick={exportRosters}
              />
            </CardFooter>
          </Column>
        </PositionedCard>
      </Wrapper>
    );
  }
);

/**
 * @description performs the filtering of a list of entities based on the supplied filters
 * @param search
 * @param teams
 * @param groups
 * @param entities
 */
const filterEntities = (
  search: string,
  teams: string[],
  groups: string[],
  entities: Entity[]
): Entity[] =>
  entities.filter((entity: Entity): boolean =>
    matchesFilter(entity, search, groups, teams)
  );

/**
 * @description determines if the given entity matches the supplied filters
 * All conditions must be satisfied (AND logic)
 * @param entity
 * @param search
 * @param groups
 * @param teams
 */
const matchesFilter = (
  entity: Entity,
  search: string,
  groups: string[],
  teams: string[]
): boolean => {
  /// check if the entity belongs to any of the filtered teams
  if (teams?.length != null && teams.length > 0) {
    const matchedTeams = intersection(teams, entity?.teams ?? []);
    if (!matchedTeams.length) return false;
  }

  /// check if the entity belongs to any of the filtered groups
  if (groups?.length != null && groups.length > 0) {
    const matchedGroups = intersection(groups, entity?.groups ?? []);
    if (!matchedGroups.length) return false;
  }

  /// check that the entity name matches the search
  if (search != null && search.length > 0) {
    if (!entity.name.toLowerCase().includes(search.toLowerCase())) return false;
  }

  return true;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DesktopContainer = styled(Column as any)`
  background: #005a7080;
  border-radius: 10px;
  grid-column: 1/6;
  grid-row: 1/25;
  @media (max-width: 991px) {
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }
`;

interface IWrapperProps {
  isMobile: boolean;
  open: boolean;
  toggle: () => void;
  children: React.ReactNode;
}

const Wrapper = (props: IWrapperProps): React.ReactElement => {
  if (props.isMobile) {
    return (
      <MobileContainer open={props.open} toggle={props.toggle}>
        {props.children}
      </MobileContainer>
    );
  }
  return <DesktopContainer>{props.children}</DesktopContainer>;
};

interface IMatchParams {
  entityType: string;
  entityId: string;
}
const MobileContainer = withRouter(
  (
    props: {
      children: React.ReactNode;
      open: boolean;
      toggle: () => void;
    } & RouteComponentProps<IMatchParams>
  ): React.ReactElement => {
    const { open, toggle } = props;
    return (
      <React.Fragment>
        <Overlay open={open} />
        <Container open={open}>
          <TriggerContainer>
            <IconButton
              title={open ? 'Close Menu' : 'Open Menu'}
              onClick={toggle}
              icon='keyboard_arrow_up'
            />
          </TriggerContainer>
          <MenuContainer open={open}>{props.children}</MenuContainer>
        </Container>
      </React.Fragment>
    );
  }
);

interface IMenuProps {
  open: boolean;
}
const MenuContainer = styled.div<IMenuProps>`
  width: 100%;
  max-height: ${(props: IMenuProps): string => (props.open ? '100%' : '0px')};
  height: ${(props: IMenuProps): string => (props.open ? '100%' : '0px')};
  opacity: ${(props: IMenuProps): number => (props.open ? 1 : 0)};
  transition-property: opacity;
  transition-duration: 0.5s;
  transition-timing-function: 'cubic-bezier(0, 1, 0.5, 1)';
  flex: ${(props: IMenuProps): number => (props.open ? 1 : 0)};
  position: relative;
`;

const TriggerContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  flex: 0;
`;

const Overlay = styled.div<IMenuProps>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 11;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props: IMenuProps): string => (props.open ? 'block' : 'none')};
`;

const Container = styled.div<IMenuProps>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  margin-left: 10px;
  margin-right: 10px;
  min-height: ${(props: IMenuProps): string => (props.open ? '85vh' : '50px')};
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: var(--color-surface-6);
  z-index: 12;
  width: calc(100% - 20px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition-property: all;
  transition-duration: 0.5s;
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  overflow: hidden;
`;

const PositionedCard = styled(Card as any)`
  @media (max-width: 990px) {
    position: absolute;
  }
`;

const SelectedEntityLabel = styled.div`
  display: none;
  @media (max-width: 1300px) {
    display: block;
    text-align: center;
    padding: 8px 16px;
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text);
    background: var(--color-surface-3);
  }
`;

const MenuTitle = styled.h6`
  margin: 10px auto;
`;
